import { createReducer } from '@reduxjs/toolkit'
import {
  CurrentUserInfo,
  LoadingStatus,
  UserAttributes,
  Resource,
  UserRole,
  NewPasswordChallenge,
  SignInUserSession,
} from 'shared/types'
import { Auth } from 'shared/services'
import { initResource, setFailed, setPending, setSucceeded } from 'shared/utils'
import {
  login,
  setNewPassword,
  logout,
  getCurrentAuthenticatedUser,
  clearErrors,
  sendForgotPasswordEmail,
  resetPassword,
  register,
  getCurrentUserInfo,
  editUserInfo,
  confirm,
  resendActivationEmail,
} from './actions'

interface State {
  isAuthenticated: boolean
  loading: LoadingStatus
  currentUserEmail: string
  error?: string | null
  register: Resource
  login: Resource<NewPasswordChallenge>
  resetPassword: Resource
  confirm: Resource
  sendForgotPasswordEmail: Resource
  currentUserInfo: Resource<CurrentUserInfo>
  setNewPassord: Resource
  resendActivationEmail: Resource
}

export const initialState: State = {
  isAuthenticated: false,
  loading: LoadingStatus.Idle,
  error: null,
  currentUserEmail: '',
  register: initResource(),
  login: initResource(),
  resetPassword: initResource(),
  confirm: initResource(),
  currentUserInfo: initResource({
    attributes: {
      [UserAttributes.name]: '',
      [UserAttributes.avatarUrl]: '',
      [UserAttributes.role]: UserRole.User,
    },
    username: '',
  }),
  sendForgotPasswordEmail: initResource(),
  setNewPassord: initResource(),
  resendActivationEmail: initResource(),
}

export default createReducer(initialState, builder =>
  builder
    .addCase(
      login.pending,
      (
        state,
        {
          meta: {
            arg: { username },
          },
        }
      ) => {
        setPending(state.login)
        state.currentUserEmail = username
        state.sendForgotPasswordEmail.error = null
        state.sendForgotPasswordEmail.loading = LoadingStatus.Idle
        state.resetPassword.loading = LoadingStatus.Idle
      }
    )
    .addCase(login.fulfilled, (state, { payload }) => {
      if (
        (payload as NewPasswordChallenge).challengeName ===
        'NEW_PASSWORD_REQUIRED'
      ) {
        setSucceeded(state.login, payload)
      } else {
        state.isAuthenticated = true
        const {
          accessToken: { jwtToken: accessToken },
          refreshToken: { token: refreshToken },
          idToken: { jwtToken: idToken },
        } = payload as SignInUserSession
        Auth.setTokensInfo({ accessToken, refreshToken, idToken })
        setSucceeded(state.login)
      }
    })
    .addCase(login.rejected, (state, action) => {
      state.isAuthenticated = false
      setFailed(state.login, action.payload)
    })
    .addCase(setNewPassword.pending, state => {
      setPending(state.setNewPassord)
    })
    .addCase(setNewPassword.fulfilled, (state, { payload }) => {
      state.isAuthenticated = true
      const {
        accessToken: { jwtToken: accessToken },
        refreshToken: { token: refreshToken },
        idToken: { jwtToken: idToken },
      } = payload
      Auth.setTokensInfo({ accessToken, refreshToken, idToken })
      setSucceeded(state.setNewPassord)
    })
    .addCase(setNewPassword.rejected, (state, action) => {
      state.isAuthenticated = false
      setFailed(state.setNewPassord, action.payload)
    })
    .addCase(register.pending, state => {
      setPending(state.register)
    })
    .addCase(register.fulfilled, state => {
      setSucceeded(state.register)
    })
    .addCase(register.rejected, (state, action) => {
      setFailed(state.register, action.payload)
    })
    .addCase(logout.pending, state => {
      state.loading = LoadingStatus.Pending
    })
    .addCase(logout.fulfilled, state => {
      state.loading = LoadingStatus.Succeeded
      state.isAuthenticated = false
      Auth.logout()
    })
    .addCase(logout.rejected, (state, action) => {
      state.loading = LoadingStatus.Failed
      state.error = action.error.message
    })
    .addCase(getCurrentAuthenticatedUser.pending, state => {
      state.loading = LoadingStatus.Pending
    })
    .addCase(getCurrentAuthenticatedUser.fulfilled, state => {
      state.loading = LoadingStatus.Succeeded
      state.isAuthenticated = true
    })
    .addCase(getCurrentAuthenticatedUser.rejected, state => {
      state.loading = LoadingStatus.Failed
      state.isAuthenticated = false
    })
    .addCase(sendForgotPasswordEmail.pending, state => {
      setPending(state.sendForgotPasswordEmail)
      state.login.error = null
      state.login.loading = LoadingStatus.Idle
      state.resetPassword.loading = LoadingStatus.Idle
    })
    .addCase(sendForgotPasswordEmail.fulfilled, state => {
      setSucceeded(state.sendForgotPasswordEmail)
    })
    .addCase(sendForgotPasswordEmail.rejected, state => {
      setFailed(state.sendForgotPasswordEmail)
    })
    .addCase(resendActivationEmail.pending, state => {
      setPending(state.resendActivationEmail)
    })
    .addCase(resendActivationEmail.fulfilled, state => {
      setSucceeded(state.resendActivationEmail)
    })
    .addCase(resendActivationEmail.rejected, state => {
      setFailed(state.resendActivationEmail)
    })
    .addCase(resetPassword.pending, state => {
      setPending(state.resetPassword)
    })
    .addCase(resetPassword.fulfilled, state => {
      setSucceeded(state.resetPassword)
    })
    .addCase(resetPassword.rejected, state => {
      setFailed(state.resetPassword)
    })
    .addCase(confirm.pending, state => {
      setPending(state.confirm)
    })
    .addCase(confirm.fulfilled, state => {
      setSucceeded(state.confirm)
    })
    .addCase(confirm.rejected, state => {
      setFailed(state.confirm)
    })
    .addCase(clearErrors, state => {
      state.error = null
    })
    .addCase(getCurrentUserInfo.pending, state => {
      setPending(state.currentUserInfo)
    })
    .addCase(getCurrentUserInfo.fulfilled, (state, { payload }) => {
      setSucceeded(state.currentUserInfo, payload)
    })
    .addCase(getCurrentUserInfo.rejected, state => {
      setFailed(state.currentUserInfo)
    })
    .addCase(editUserInfo.pending, state => {
      setPending(state.currentUserInfo)
    })
    .addCase(
      editUserInfo.fulfilled,
      (
        state,
        {
          meta: {
            arg: { name },
          },
        }
      ) => {
        state.currentUserInfo.data.attributes.name = name
        setSucceeded(state.currentUserInfo)
      }
    )
    .addCase(editUserInfo.rejected, state => {
      setFailed(state.currentUserInfo)
    })
)
